import crypto from 'crypto';

// Encryption configuration
const ALGORITHM = 'aes-256-gcm';
const IV_LENGTH = 16; // For AES, this is always 16 bytes

/**
 * @description Encrypt a string using AES-256-GCM (server-side only).
 * Throws when the encryption key is missing or the underlying crypto call
 * fails — silently returning plaintext on a misconfigured server would
 * persist unencrypted API keys into the JWT, which is strictly worse than
 * failing the request.
 *
 * @param text The text to encrypt
 * @param encryptionKey Optional custom encryption key. If not provided, uses
 *   AUTH_API_KEY_ENCRYPTION_SECRET from environment.
 * @throws {Error} when the encryption key is unavailable or encryption fails.
 * @returns The encrypted text as `base64(iv):base64(ciphertext):base64(authTag)`.
 */
export const encryptServerSide = (text: string, encryptionKey?: string): string => {
  const secretKey = encryptionKey || process.env.AUTH_API_KEY_ENCRYPTION_SECRET;
  if (!secretKey) {
    throw new Error('encryptServerSide: AUTH_API_KEY_ENCRYPTION_SECRET is not set and no key was provided');
  }

  const key = crypto.createHash('sha256').update(secretKey).digest();
  const iv = crypto.randomBytes(IV_LENGTH);
  const cipher = crypto.createCipheriv(ALGORITHM, key, iv);

  let encrypted = cipher.update(text, 'utf8', 'base64');
  encrypted += cipher.final('base64');
  const authTag = cipher.getAuthTag();

  return `${iv.toString('base64')}:${encrypted}:${authTag.toString('base64')}`;
};

/**
 * @description Decrypt a string produced by `encryptServerSide` (server-side only).
 * Inputs that are NOT in the expected `iv:ciphertext:tag` shape are passed
 * through unchanged — that lets callers route both legacy plain values and
 * newly-encrypted ones through this function. Throws when the input IS in
 * the expected shape but the key is missing or decryption fails, so that a
 * misconfigured server cannot silently send ciphertext as the Authorization
 * header.
 *
 * @param encryptedText The encrypted text (format: base64(iv):base64(ciphertext):base64(authTag)).
 * @param encryptionKey Optional custom encryption key. If not provided, uses
 *   AUTH_API_KEY_ENCRYPTION_SECRET from environment.
 * @throws {Error} when the input looks encrypted but the key is missing or decryption fails.
 * @returns The decrypted text, or the input unchanged when it is not in the encrypted shape.
 */
export const decryptServerSide = (encryptedText: string, encryptionKey?: string): string => {
  const parts = encryptedText.split(':');
  if (parts.length !== 3) {
    return encryptedText;
  }

  const secretKey = encryptionKey || process.env.AUTH_API_KEY_ENCRYPTION_SECRET;
  if (!secretKey) {
    throw new Error('decryptServerSide: AUTH_API_KEY_ENCRYPTION_SECRET is not set and no key was provided');
  }

  const key = crypto.createHash('sha256').update(secretKey).digest();
  const iv = Buffer.from(parts[0], 'base64');
  const encrypted = parts[1];
  const authTag = Buffer.from(parts[2], 'base64');

  const decipher = crypto.createDecipheriv(ALGORITHM, key, iv);
  decipher.setAuthTag(authTag);

  let decrypted = decipher.update(encrypted, 'base64', 'utf8');
  decrypted += decipher.final('utf8');
  return decrypted;
};
